'use client';

import { useCallback } from 'react';

import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import {
  buildPathWithQuery,
  buildUrlWithPersistedParams,
} from '@/utils/persisted-params.util';

export interface BuildLinkOptions {
  path: string;
  query?: Record<string, string | string[]>;
  overrides?: Partial<Record<string, string | null>>;
}

export function useNavigation() {
  const searchParams = useSearchParams(Object.values(PersistedUserNavigationSearchParams));
  const city = searchParams[PersistedUserNavigationSearchParams.CIDADE];

  const buildLink = useCallback(
    (options: BuildLinkOptions): string => {
      const { path, query, overrides } = options;
      const baseUrl = buildPathWithQuery(path, query);
      const paramsRecord = Object.fromEntries(
        Object.values(PersistedUserNavigationSearchParams).map((key) => [key, searchParams[key] ?? null]),
      ) as Record<string, string | null>;
      return buildUrlWithPersistedParams({ baseUrl, overrides, searchParams: paramsRecord });
    },
    [searchParams],
  );

  const getHomeLink = useCallback((): string => {
    const basePath = city ? `/cidades/${city}/` : '/';
    return buildLink({ path: basePath });
  }, [city, buildLink]);

  const resolveHref = useCallback(
    (href: string): string => (href.startsWith('/') ? buildLink({ path: href }) : href),
    [buildLink],
  );

  return {
    buildLink,
    resolveHref,
    getHomeLink,
  };
}
